import React, { useState, useEffect } from 'react';
import { Shield, File, Share2, User, Lock, Eye, AlertCircle, Play, RotateCcw, Ghost, HardDrive, Database, Globe } from 'lucide-react';

type AttackType = 'none' | 'ransomware' | 'worm' | 'spyware' | 'trojan';

const HEX_CHARS = ['0xFA', '0x99', '0x2B', '0xFF', '0x01', '0xDE', '0xAD', '0xBE', '0xEF', '0x7C', '0x14', '0x88'];

export const Sandbox: React.FC = () => {
  const [activeAttack, setActiveAttack] = useState<AttackType>('none');
  const [status, setStatus] = useState("System Healthy");
  const [logs, setLogs] = useState<string[]>(["[SYSTEM] Kernel loaded.", "[SYSTEM] Network interface active."]);

  const addLog = (msg: string) => {
    setLogs(prev => [...prev.slice(-4), `[${new Date().toLocaleTimeString()}] ${msg}`]);
  };

  const handleAttack = (type: AttackType) => {
    if (activeAttack !== 'none') return;
    setActiveAttack(type);
    
    switch(type) {
      case 'ransomware':
        setStatus("CRITICAL: Files Encrypted");
        addLog("Unexpected process 'enc_svc.exe' detected.");
        addLog("Bulk file modification detected in C:/Users/Documents");
        break;
      case 'worm':
        setStatus("WARNING: Network Traffic Spike");
        addLog("Outbound connection request on Port 445.");
        addLog("Attempting to copy self to 192.168.1.5...");
        break;
      case 'spyware':
        setStatus("CAUTION: Data Leak Detected");
        addLog("Keylogger hook active in 'winlogon.exe'.");
        addLog("Exfiltrating credentials to C&C Server...");
        break;
      case 'trojan':
        setStatus("WARNING: Suspicious Process");
        addLog("User executed 'SuperGame.exe'.");
        addLog("Hidden background process spawned: 'backdoor.exe'");
        break;
    }
  };

  const reset = () => {
    setActiveAttack('none');
    setStatus("System Healthy");
    setLogs(["[SYSTEM] Reboot successful.", "[SYSTEM] Security scan complete: Clean."]);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-12">
      <style>{`
        @keyframes packet-move {
          0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
          20% { opacity: 1; scale(1.2); }
          80% { opacity: 1; scale(1.2); }
          100% { transform: translate(calc(-50% + var(--x)), calc(-50% + var(--y))) scale(0.5); opacity: 0; }
        }
        @keyframes encryption-float {
          0% { transform: translateY(0) rotate(0deg); opacity: 0; }
          20% { opacity: 0.8; }
          80% { opacity: 0.8; }
          100% { transform: translateY(-140px) rotate(15deg); opacity: 0; }
        }
        @keyframes scan-line {
          0% { top: 0; }
          100% { top: 100%; }
        }
        @keyframes glitch-ghost {
          0%, 100% { opacity: 0.4; transform: translate(0, 0); }
          33% { opacity: 0.8; transform: translate(-2px, 1px); }
          66% { opacity: 0.2; transform: translate(2px, -1px); }
        }
        @keyframes pulse-red-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(220, 38, 38, 0.4); }
          50% { box-shadow: 0 0 50px rgba(220, 38, 38, 0.9); }
        }
        .hex-particle {
          position: absolute;
          font-family: monospace;
          font-weight: bold;
          color: #f87171;
          pointer-events: none;
          animation: encryption-float 2s infinite ease-out;
        }
        .scan-effect {
          position: absolute;
          left: 0;
          width: 100%;
          height: 2px;
          background: rgba(147, 51, 234, 0.5);
          box-shadow: 0 0 10px rgba(147, 51, 234, 0.8);
          animation: scan-line 2s infinite linear;
        }
        .animate-packet {
          animation: packet-move 1.2s infinite ease-in;
        }
        .animate-ghost-glitch {
          animation: glitch-ghost 0.2s infinite;
        }
        .animate-pulse-red {
          animation: pulse-red-glow 1.5s infinite ease-in-out;
        }
      `}</style>

      <div className="grid lg:grid-cols-3 gap-8">
        
        {/* Controls Panel */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-200">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-cyber-dark">
              <Play size={20} className="text-cyber-accent" /> System Simulator
            </h2>
            <div className="space-y-3">
              {[
                { id: 'ransomware', label: 'Ransomware', color: 'bg-red-500', desc: 'Encrypts storage for ransom' },
                { id: 'worm', label: 'Network Worm', color: 'bg-orange-500', desc: 'Auto-spreads via network' },
                { id: 'spyware', label: 'Spyware', color: 'bg-purple-500', desc: 'Exfiltrates user data' },
                { id: 'trojan', label: 'Trojan Horse', color: 'bg-blue-600', desc: 'Hidden administrative access' }
              ].map(atk => (
                <button
                  key={atk.id}
                  onClick={() => handleAttack(atk.id as AttackType)}
                  disabled={activeAttack !== 'none'}
                  className={`w-full p-4 rounded-xl text-left transition-all flex flex-col border-2 ${
                    activeAttack === atk.id 
                      ? `${atk.color} text-white border-transparent scale-105 shadow-xl` 
                      : 'bg-white text-slate-700 border-slate-100 hover:border-slate-300 disabled:opacity-50'
                  }`}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="font-bold text-lg">{atk.label}</span>
                    {activeAttack === atk.id && <AlertCircle size={18} className="animate-pulse" />}
                  </div>
                  <span className={`text-xs ${activeAttack === atk.id ? 'text-white/80' : 'text-slate-400'}`}>
                    {atk.desc}
                  </span>
                </button>
              ))}
            </div>
            
            <button 
              onClick={reset}
              className="mt-6 w-full flex items-center justify-center gap-2 py-3 border-2 border-slate-200 rounded-xl font-bold text-slate-500 hover:bg-slate-50 transition-colors"
            >
              <RotateCcw size={18} /> Reboot System
            </button>
          </div>

          <div className="bg-slate-900 text-green-400 p-4 rounded-xl font-mono text-[10px] md:text-xs h-48 overflow-hidden shadow-inner border border-slate-800">
            <p className="text-slate-500 border-b border-slate-800 pb-2 mb-2 uppercase tracking-tighter font-bold flex justify-between">
              <span>Kernel Events</span>
              <span className="text-green-500 animate-pulse">● MONITORING</span>
            </p>
            {logs.map((log, i) => <p key={i} className="mb-1 leading-tight">{log}</p>)}
          </div>
        </div>

        {/* Visualization Panel */}
        <div className="lg:col-span-2 relative bg-slate-950 rounded-[2.5rem] shadow-2xl border-8 border-slate-900 overflow-hidden flex items-center justify-center p-8 min-h-[500px]">
          
          {/* Cyber Grid Background */}
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
            backgroundImage: `linear-gradient(#475569 1px, transparent 1px), linear-gradient(90deg, #475569 1px, transparent 1px)`,
            backgroundSize: '40px 40px'
          }}></div>

          {/* Top Status Bar */}
          <div className={`absolute top-0 left-0 w-full h-2 transition-colors duration-700 ${
            activeAttack === 'none' ? 'bg-green-500' : 'bg-red-600 animate-pulse'
          }`}></div>

          <div className={`absolute top-8 left-8 px-5 py-2 rounded-full font-bold text-xs tracking-widest shadow-lg transition-all flex items-center gap-3 border ${
            activeAttack === 'none' ? 'bg-green-500/10 text-green-500 border-green-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'
          }`}>
            <div className={`w-2.5 h-2.5 rounded-full ${activeAttack === 'none' ? 'bg-green-500' : 'bg-red-500 animate-ping'}`}></div>
            {status.toUpperCase()}
          </div>

          {/* Components Architecture */}
          <div className="grid grid-cols-2 gap-20 md:gap-32 relative z-10 w-full max-w-lg">
            
            {/* Component: Local Storage (Ransomware) */}
            <div className="relative flex flex-col items-center gap-4">
              <div className={`w-28 h-28 rounded-[2rem] flex items-center justify-center transition-all duration-700 relative border-2 ${
                activeAttack === 'ransomware' 
                  ? 'bg-red-600 text-white animate-pulse-red scale-110 border-transparent' 
                  : 'bg-slate-900 text-slate-500 border-slate-800 shadow-xl'
              }`}>
                {activeAttack === 'ransomware' ? <Lock size={48} className="animate-bounce" /> : <Database size={48} />}
                
                {activeAttack === 'ransomware' && HEX_CHARS.map((val, idx) => (
                  <div 
                    key={idx} 
                    className="hex-particle" 
                    style={{ 
                      left: `${(idx * 15) % 80 + 10}%`, 
                      animationDelay: `${idx * 0.2}s`,
                      fontSize: `${(idx % 3) * 2 + 8}px`
                    }}
                  >
                    {val}
                  </div>
                ))}
              </div>
              <span className={`font-black uppercase text-[10px] tracking-[0.2em] ${activeAttack === 'ransomware' ? 'text-red-500' : 'text-slate-600'}`}>Local Storage</span>
            </div>

            {/* Component: Network Hub (Worm) */}
            <div className="relative flex flex-col items-center gap-4">
              <div className={`w-28 h-28 rounded-[2rem] flex items-center justify-center transition-all duration-700 border-2 ${
                activeAttack === 'worm' 
                  ? 'bg-orange-500 text-white shadow-[0_0_60px_rgba(249,115,22,0.6)] scale-110 border-transparent' 
                  : 'bg-slate-900 text-slate-500 border-slate-800 shadow-xl'
              }`}>
                <Share2 size={48} className={activeAttack === 'worm' ? 'animate-spin' : ''} />
                
                {/* Worm Propagation Packets */}
                {activeAttack === 'worm' && [
                  {x: '-250px', y: '0'},    // Towards Storage
                  {x: '-250px', y: '250px'}, // Towards Vault
                  {x: '0', y: '250px'},      // Towards Kernel
                  {x: '-100px', y: '100px'}, // Random spread
                  {x: '-150px', y: '50px'},  // Random spread
                  {x: '50px', y: '150px'},   // Random spread
                ].map((pos, i) => (
                  <div key={i} className="absolute top-1/2 left-1/2 w-2.5 h-2.5 bg-orange-300 rounded-full animate-packet shadow-[0_0_10px_#fdba74]" style={{
                    '--x': pos.x, '--y': pos.y,
                    animationDelay: `${i * 0.2}s`
                  } as any}></div>
                ))}
              </div>
              <span className={`font-black uppercase text-[10px] tracking-[0.2em] ${activeAttack === 'worm' ? 'text-orange-500' : 'text-slate-600'}`}>Network Hub</span>
            </div>

            {/* Component: User Identity (Spyware) */}
            <div className="relative flex flex-col items-center gap-4">
              <div className={`w-28 h-28 rounded-[2rem] flex items-center justify-center transition-all duration-700 border-2 relative overflow-hidden ${
                activeAttack === 'spyware' 
                  ? 'bg-purple-600 text-white shadow-[0_0_60px_rgba(147,51,234,0.6)] scale-110 border-transparent' 
                  : 'bg-slate-900 text-slate-500 border-slate-800 shadow-xl'
              }`}>
                {activeAttack === 'spyware' ? <Eye size={48} className="animate-pulse" /> : <User size={48} />}
                {activeAttack === 'spyware' && <div className="scan-effect"></div>}
              </div>
              <span className={`font-black uppercase text-[10px] tracking-[0.2em] ${activeAttack === 'spyware' ? 'text-purple-500' : 'text-slate-600'}`}>User Vault</span>
              
              {/* Exfiltration Visuals */}
              {activeAttack === 'spyware' && (
                <div className="absolute -top-12 -right-12">
                   <Globe size={40} className="text-purple-400 animate-pulse" />
                   <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-purple-400 rounded-full animate-packet" style={{ '--x': '60px', '--y': '-60px' } as any}></div>
                </div>
              )}
            </div>

            {/* Component: System Kernel (Trojan) */}
            <div className="relative flex flex-col items-center gap-4">
              <div className={`w-28 h-28 rounded-[2rem] flex items-center justify-center transition-all duration-700 border-2 relative ${
                activeAttack === 'trojan' 
                  ? 'bg-blue-600 text-white shadow-[0_0_60px_rgba(37,99,235,0.6)] scale-110 border-transparent' 
                  : 'bg-slate-900 text-slate-500 border-slate-800 shadow-xl'
              }`}>
                <HardDrive size={48} />
                
                {/* Glitchy Ghost for Trojan persistence */}
                {activeAttack === 'trojan' && (
                  <Ghost 
                    size={52} 
                    className="absolute -top-6 -right-6 text-blue-200 animate-ghost-glitch" 
                  />
                )}
              </div>
              <span className={`font-black uppercase text-[10px] tracking-[0.2em] ${activeAttack === 'trojan' ? 'text-blue-500' : 'text-slate-600'}`}>Kernel Core</span>
            </div>

          </div>

          {/* Global Alert Flicker */}
          {activeAttack !== 'none' && (
            <div className="absolute inset-0 bg-red-900/5 mix-blend-screen animate-pulse pointer-events-none"></div>
          )}
        </div>

      </div>

      <div className="mt-12 bg-white p-10 rounded-[2.5rem] shadow-xl border border-slate-100">
        <h3 className="text-2xl font-bold mb-8 text-cyber-dark tracking-tight">Technical Simulation Briefing</h3>
        <div className="grid md:grid-cols-2 gap-10">
          <div className="flex gap-5 items-start">
            <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex-shrink-0 flex items-center justify-center font-black shadow-sm">01</div>
            <div>
              <h4 className="font-bold text-slate-900 text-lg mb-2">Encryption Mechanics</h4>
              <p className="text-sm text-slate-600 leading-relaxed">During the <strong>Ransomware</strong> phase, data is rapidly jumbled into hexadecimal strings. The storage unit pulses as it performs intensive CPU tasks to lock every file.</p>
            </div>
          </div>
          <div className="flex gap-5 items-start">
            <div className="w-12 h-12 rounded-2xl bg-orange-50 text-orange-600 flex-shrink-0 flex items-center justify-center font-black shadow-sm">02</div>
            <div>
              <h4 className="font-bold text-slate-900 text-lg mb-2">Autonomous Replication</h4>
              <p className="text-sm text-slate-600 leading-relaxed">The <strong>Network Worm</strong> simulation shows high-speed "packets" attempting to find weaknesses in other system nodes. It doesn't need user interaction to move.</p>
            </div>
          </div>
          <div className="flex gap-5 items-start">
            <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex-shrink-0 flex items-center justify-center font-black shadow-sm">03</div>
            <div>
              <h4 className="font-bold text-slate-900 text-lg mb-2">Data Exfiltration</h4>
              <p className="text-sm text-slate-600 leading-relaxed"><strong>Spyware</strong> "scans" the User Vault for credentials. Once captured, small data packets are secretly streamed to a remote Command & Control globe icon.</p>
            </div>
          </div>
          <div className="flex gap-5 items-start">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex-shrink-0 flex items-center justify-center font-black shadow-sm">04</div>
            <div>
              <h4 className="font-bold text-slate-900 text-lg mb-2">Persistent Backdoors</h4>
              <p className="text-sm text-slate-600 leading-relaxed">The <strong>Trojan</strong> "Ghost" represents an attacker maintaining a hidden presence. It glitches because it's actively evading detection by standard system checks.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
